// charting.service.ts
import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import type { VisitNoteAudience, VisitNotePdfLink } from '../../dtos/patientmr/visit.dto';
import {
  EntryType,
  BaseEntryData,
  VitalsData,
  MedicationData,
  GeneralNoteData,
  WoundCareData,
  IncidentData,
  MeasurementData,
  VisitSummaryData,
  FeedData,
  EntryData,
  ChartEntry,
  ChartEntriesResponse,
  ChartEntryInvalidationInfo,
  ChartMeta,
  ChartReopenRequest,
  ChartVersionSnapshot
} from '../../dtos/patientmr/charting';
import { Offline } from '../offline/offlineServices';
import { OfflineMode, runWithOfflineBranch } from '../offline/offlineMode';
import { ChartRepo } from '../offline/repos/chartRepo';
import { ulid } from '../offline/ids';

// Re-export types for backward compatibility
export type {
  EntryType,
  BaseEntryData,
  VitalsData,
  MedicationData,
  GeneralNoteData,
  WoundCareData,
  IncidentData,
  MeasurementData,
  VisitSummaryData,
  FeedData,
  EntryData,
  ChartEntry,
  ChartEntriesResponse,
  ChartEntryInvalidationInfo,
  ChartMeta,
  ChartReopenRequest,
  ChartVersionSnapshot
};

// Response interface for chart entries API (internal use)
interface ChartEntriesApiResponse {
  entries: any[];
  chartingStatus: string;
  chart?: ChartMeta;
  /** Phase 18 M7 */
  lastGivenByMedication?: Record<string, string>;
}

// Request payload interfaces
export interface CreateEntryPayload {
  entryType: EntryType;
  data: EntryData;
  /** Phase 9C: required (400 REASON_REQUIRED) when the author changes an entry written before the chart was first submitted */
  amendReason?: string;
  /** Phase 16C: when the care happened (defaults to now on the server; limited to the visit window) */
  observedAt?: string;
  /** Phase 18 M6: reason when a supervisor changes the visibility of another author's entry */
  reason?: string;
}

/** Phase 16C: optimistic version check on updates (expectedVersion = the version being edited). */
export interface UpdateEntryOptions {
  expectedVersion?: number;
}

// Visit interface for historical visits
export interface Visit {
  id: string;
  date: string;
  type: string;
}

/**
 * Finalize a visit's chart
 * @param patientId The patient ID
 * @param visitId The visit ID to finalize
 * @returns A Promise that resolves when the chart is finalized
 */
export const finalizeChart = async (patientId: string, visitId: string): Promise<void> => {
  const response: ApiResponse<void> = await apiClient.put(
    `/patientmr/${patientId}/visits/${visitId}/chart/finalize`
  );

  if (response.success) {
    return;
  }

  throw new Error(response.error?.message || 'Failed to finalize chart');
};

/**
 * Submit a visit's chart
 * @param patientId The patient ID
 * @param visitId The visit ID to submit
 * @returns A Promise that resolves when the chart is submitted
 */
export const submitChart = async (patientId: string, visitId: string): Promise<void> => runWithOfflineBranch({
  visitId,
  local: () => Offline.local.submitChart(patientId, visitId),
  online: async () => {
    const response: ApiResponse<void> = await apiClient.put(
      `/patientmr/${patientId}/visits/${visitId}/chart/submit`
    );
    if (response.success) {
      if (OfflineMode.enabled()) await Offline.visits.setChartingStatus(visitId, 'SUBMITTED', 'synced').catch(() => undefined);
      return;
    }
    throw new Error(response.error?.message || 'Failed to submit chart');
  },
});

/**
 * Reopen a visit's chart (SUBMITTED/QA/COMPLETE -> DRAFT). The server requires a reason.
 * @param patientId The patient ID
 * @param visitId The visit ID to reopen
 * @param reason Why the chart is being reopened or rejected (at least 5 characters)
 * @returns A Promise that resolves when the chart is reopened
 */
export const reopenChart = async (patientId: string, visitId: string, reason: string): Promise<void> => {
  const response: ApiResponse<void> = await apiClient.put(
    `/patientmr/${patientId}/visits/${visitId}/chart/reopen`,
    { reason }
  );

  if (response.success) {
    return;
  }

  throw new Error(response.error?.message || 'Failed to reopen chart');
};

/**
 * Start QA process for a visit's chart
 * @param patientId The patient ID
 * @param visitId The visit ID to start QA for
 * @returns A Promise that resolves when QA is started
 */
export const startQAChart = async (patientId: string, visitId: string): Promise<void> => {
  const response: ApiResponse<void> = await apiClient.put(
    `/patientmr/${patientId}/visits/${visitId}/chart/startQA`
  );

  if (response.success) {
    return;
  }

  throw new Error(response.error?.message || 'Failed to start QA for chart');
};

/**
 * Fetch all chart entries for a visit
 * @param patientId The patient ID
 * @param visitId The visit ID
 * @returns A Promise that resolves to an array of ChartEntry objects
 */
export const fetchChartEntries = async (patientId: string, visitId: string): Promise<ChartEntry[]> => {
  // Add timestamp to bust cache
  const timestamp = new Date().getTime();
  const response: ApiResponse<ChartEntriesApiResponse> = await apiClient.get(
    `/patientmr/${patientId}/visits/${visitId}/chart`,
    { _t: timestamp }
  );

  if (response.success && response.data) {
    const entries = response.data.entries || response.data;
    return Array.isArray(entries) ? formatChartEntries(entries) : [];
  }

  throw new Error(response.error?.message || 'Failed to fetch chart entries');
};

/**
 * Fetch chart entries with charting status for a visit
 * @param patientId The patient ID
 * @param visitId The visit ID
 * @returns A Promise that resolves to chart entries and charting status
 */
export const fetchChartEntriesWithStatus = async (patientId: string, visitId: string): Promise<ChartEntriesResponse> => runWithOfflineBranch({
  visitId,
  // Phase 16C: the device copy (cached server entries + unsynced local ones, marked by syncState)
  local: async () => {
    const { entries, chartingStatus } = await Offline.local.listChart(visitId);
    return { chartEntries: formatChartEntries(entries.map(ChartRepo.toApiShape)), chartingStatus };
  },
  online: async () => {
    // Add timestamp to bust cache
    const timestamp = new Date().getTime();
    const response: ApiResponse<ChartEntriesApiResponse> = await apiClient.get(
      `/patientmr/${patientId}/visits/${visitId}/chart`,
      { _t: timestamp }
    );

    if (response.success && response.data) {
      const entries = response.data.entries || [];
      const chartingStatus = response.data.chartingStatus || 'NOT_STARTED';
      if (OfflineMode.enabled() && Array.isArray(entries)) {
        await Offline.charts.cacheServer(visitId, patientId, entries).catch(() => undefined);
      }
      return {
        chartEntries: Array.isArray(entries) ? formatChartEntries(entries) : [],
        chartingStatus,
        chart: response.data.chart,
        lastGivenByMedication: toLastGiven(response.data.lastGivenByMedication)
      };
    }

    throw new Error(response.error?.message || 'Failed to fetch chart entries with status');
  },
});

/**
 * Create a new chart entry
 * @param patientId The patient ID
 * @param visitId The visit ID
 * @param entryData The entry data payload
 * @returns A Promise that resolves to a ChartEntry object
 */
export const createChartEntry = async (patientId: string, visitId: string, entryData: CreateEntryPayload): Promise<ChartEntry> => {
  // Phase 16C: client ULID as the entry id and Idempotency-Key; a timeout is queued with the same entry id
  const entryId = ulid();
  return runWithOfflineBranch({
    visitId,
    local: async () => formatChartEntry(ChartRepo.toApiShape(await Offline.local.createChartEntry(patientId, visitId, entryData, entryId))),
    online: async () => {
      const response: ApiResponse<any> = await apiClient.post(
        `/patientmr/${patientId}/visits/${visitId}/chart`,
        entryData,
        // 16B: the entry id defaults to the Idempotency-Key
        { idempotencyKey: entryId }
      );
      if (response.success && response.data) {
        if (OfflineMode.enabled()) await Offline.charts.cacheServer(visitId, patientId, [response.data]).catch(() => undefined);
        return formatChartEntry(response.data);
      }
      throw new Error(response.error?.message || 'Failed to create chart entry');
    },
  });
};

/**
 * Update an existing chart entry
 * @param patientId The patient ID
 * @param visitId The visit ID
 * @param entryId The entry ID to update
 * @param entryData The updated entry data
 * @returns A Promise that resolves to the updated ChartEntry object
 */
export const updateChartEntry = async (patientId: string, visitId: string, entryId: string, entryData: CreateEntryPayload, options: UpdateEntryOptions = {}): Promise<ChartEntry> => runWithOfflineBranch({
  visitId,
  local: async () => formatChartEntry(ChartRepo.toApiShape(await Offline.local.updateChartEntry(patientId, visitId, entryId, entryData))),
  online: async () => {
    const response: ApiResponse<any> = await apiClient.put(
      `/patientmr/${patientId}/visits/${visitId}/chart/${entryId}`,
      { ...entryData, ...(options.expectedVersion !== undefined ? { expectedVersion: options.expectedVersion } : {}) }
    );
    if (response.success && response.data) {
      if (OfflineMode.enabled()) await Offline.charts.cacheServer(visitId, patientId, [response.data]).catch(() => undefined);
      return formatChartEntry(response.data);
    }
    throw new Error(response.error?.message || 'Failed to update chart entry');
  },
});

/**
 * Flag a chart entry
 * @param patientId The patient ID
 * @param visitId The visit ID
 * @param entryId The entry ID to flag
 */
export const flagChartEntry = async (patientId: string, visitId: string, entryId: string): Promise<void> => {
  const response: ApiResponse<void> = await apiClient.post(
    `/patientmr/${patientId}/visits/${visitId}/chart/${entryId}/flag`
  );

  if (response.success) {
    return;
  }

  throw new Error(response.error?.message || 'Failed to flag chart entry');
};

/**
 * Invalidate a chart entry. Entries are never deleted: the server marks the entry INVALIDATED
 * and stores `invalidationInfo { by, at, reason }`.
 * @param patientId The patient ID
 * @param visitId The visit ID
 * @param entryId The entry ID to invalidate
 * @param invalidationReason Why the entry is invalid (at least 5 characters)
 */
export const invalidateChartEntry = async (
  patientId: string,
  visitId: string,
  entryId: string,
  invalidationReason: string
): Promise<void> => {
  const response: ApiResponse<void> = await apiClient.put(
    `/patientmr/${patientId}/visits/${visitId}/chart/${entryId}`,
    { invalidate: true, invalidationReason }
  );

  if (response.success) {
    return;
  }

  throw new Error(response.error?.message || 'Failed to invalidate chart entry');
};

/**
 * Add a supervisor addendum to a chart (Phase 9C). Any chart status; never by the visit clinician.
 * @param note The addendum text (at least 5 characters)
 * @param refEntryId Optional entry the addendum refers to
 */
export const addChartAddendum = async (patientId: string, visitId: string, note: string, refEntryId?: string): Promise<ChartEntry> => {
  const response: ApiResponse<any> = await apiClient.post(
    `/patientmr/${patientId}/visits/${visitId}/chart/addendum`,
    { note, ...(refEntryId ? { refEntryId } : {}) }
  );
  if (response.success && response.data) {
    return formatChartEntry(response.data);
  }
  throw new Error(response.error?.message || 'Failed to add the addendum');
};

/**
 * Ask to reopen a COMPLETE chart (the visit clinician or a clinical manager). A supervisor who
 * is not the requester decides. 409 VISIT_BILLED once the visit is billed.
 */
export const requestChartReopen = async (patientId: string, visitId: string, reason: string): Promise<ChartReopenRequest | null> => {
  const response: ApiResponse<{ chartReopenRequest?: ChartReopenRequest }> = await apiClient.post(
    `/patientmr/${patientId}/visits/${visitId}/chart/reopen-request`,
    { reason }
  );
  if (response.success) {
    return response.data?.chartReopenRequest || null;
  }
  throw new Error(response.error?.message || 'Failed to request the reopen');
};

/**
 * Approve or reject a pending reopen request (clinical supervisor, not the requester).
 * Approval moves the chart back to DRAFT and keeps the completed version read-only.
 * A rejection needs a comment.
 */
export const decideChartReopen = async (patientId: string, visitId: string, approve: boolean, comment?: string): Promise<void> => {
  const response: ApiResponse<void> = await apiClient.put(
    `/patientmr/${patientId}/visits/${visitId}/chart/reopen-request/decision`,
    { approve, ...(comment ? { comment } : {}) }
  );
  if (response.success) {
    return;
  }
  throw new Error(response.error?.message || 'Failed to decide the reopen');
};

/**
 * Earlier read-only versions of a chart kept by approved reopens (newest first)
 */
export const fetchChartVersions = async (patientId: string, visitId: string): Promise<ChartVersionSnapshot[]> => {
  const response: ApiResponse<{ versions: ChartVersionSnapshot[] }> = await apiClient.get(
    `/patientmr/${patientId}/visits/${visitId}/chart/versions`
  );
  if (response.success && response.data) {
    return Array.isArray(response.data.versions) ? response.data.versions : [];
  }
  throw new Error(response.error?.message || 'Failed to load chart versions');
};

/**
 * Phase 18 M9: the signed visit note PDF as a 5-minute link. `internal` (default) includes
 * INTERNAL entries; `external` leaves them out and needs a purpose (10–500 chars) and
 * admin:export:patient (the Phase 14 export permission). Same read router as the versions view (`chart/{view}`).
 */
export const getVisitNotePdf = async (
  patientId: string,
  visitId: string,
  options: { audience?: VisitNoteAudience; purpose?: string } = {}
): Promise<VisitNotePdfLink> => {
  const audience = options.audience || 'internal';
  const params = new URLSearchParams({ audience });
  if (audience === 'external' && options.purpose) params.set('purpose', options.purpose.trim());
  const response: ApiResponse<VisitNotePdfLink> = await apiClient.get(
    `/patientmr/${patientId}/visits/${visitId}/chart/pdf?${params.toString()}`
  );
  if (response.success && response.data?.url) return response.data;
  throw new Error(response.error?.message || 'Failed to prepare the visit note');
};

/**
 * Helper function to format chart entries from API to match internal structure
 * @param entries Array of entries from API
 * @returns Array of formatted ChartEntry objects
 */
const formatChartEntries = (entries: any[]): ChartEntry[] => {
  return entries.map(entry => formatChartEntry(entry));
};

/**
 * Helper function to format a single chart entry
 * @param entry Entry object from API
 * @returns Formatted ChartEntry object
 */
const formatChartEntry = (entry: any): ChartEntry => {
  // Map the API entry type to our internal type for backward compatibility
  const typeMap: { [key in EntryType]: string } = {
    'VITALS': 'vitals',
    'MEDICATION': 'mar',
    'GENERAL_NOTE': 'notes',
    'WOUND_CARE': 'wound',
    'INCIDENT': 'incident',
    'MEASUREMENT': 'measurements',
    'VISIT_SUMMARY': 'summary',
    'FEED': 'feed',
    'ADDENDUM': 'addendum',
    'RESPIRATORY': 'respiratory',
    'SEIZURE': 'seizure',
    'OUTPUT': 'output',
    'SUPERVISORY_NOTE': 'supervisory',
    'THERAPY_TREATMENT': 'therapy',
    'AIDE_TASKS': 'aidetasks',
    'PSYCHOSOCIAL_NOTE': 'psychosocial_note',
    'RT_TREATMENT': 'rt_treatment',
    'NUTRITION_NOTE': 'nutrition_note',
    'BH_SESSION': 'bh_session',
    'SYMPTOM': 'symptom'
  };

  return {
    id: entry.entryId,
    entryType: entry.entryType,
    data: entry.data,
    date: entry.timestamp || new Date().toLocaleString(),
    provider: entry.provider || 'Current Provider',
    lastEdited: entry.lastEdited,
    type: typeMap[entry.entryType], // Add the type field for backward compatibility
    flagged: entry.flagged || false,
    invalidated: entry.invalidated === true || entry.status === 'INVALIDATED',
    status: typeof entry.status === 'string' ? entry.status : undefined,
    invalidationInfo: toInvalidationInfo(entry),
    lateEntry: entry.lateEntry === true,
    minutesAfterEnd: typeof entry.minutesAfterEnd === 'number' ? entry.minutesAfterEnd : null,
    version: typeof entry.version === 'number' ? entry.version : 1,
    amended: entry.amended === true,
    lastAmendment: entry.lastAmendment || null,
    versions: Array.isArray(entry.versions) ? entry.versions : [],
    addendum: entry.addendum || null,
    createdBy: entry.createdBy || entry.providerId,
    observedAt: typeof entry.observedAt === 'string' ? entry.observedAt : undefined,
    documentedAt: typeof entry.documentedAt === 'string' ? entry.documentedAt : undefined,
    syncState: typeof entry.syncState === 'string' ? entry.syncState : undefined,
    editVersion: typeof entry.editVersion === 'number' ? entry.editVersion : undefined
  };
};

/** Phase 18 M7: keep only string timestamps keyed by medication id */
const toLastGiven = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== 'object') return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter((kv): kv is [string, string] => typeof kv[1] === 'string')
  );
};

/**
 * Read the invalidation record, tolerating older shapes (nested `invalidationInfo.invalidatedBy`/
 * `invalidatedAt`, or flat `invalidatedBy`/`invalidatedAt`/`invalidationReason` fields) and missing values.
 */
const toInvalidationInfo = (entry: any): ChartEntryInvalidationInfo | undefined => {
  const info = entry?.invalidationInfo && typeof entry.invalidationInfo === 'object' ? entry.invalidationInfo : {};
  const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);
  const result: ChartEntryInvalidationInfo = {
    by: str(info.by) ?? str(info.invalidatedBy) ?? str(entry?.invalidatedBy),
    byName: str(info.byName) ?? str(entry?.invalidatedByName),
    at: str(info.at) ?? str(info.invalidatedAt) ?? str(entry?.invalidatedAt),
    reason: str(info.reason) ?? str(entry?.invalidationReason),
  };
  return result.by || result.byName || result.at || result.reason ? result : undefined;
};
